import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PostingCard } from "./postingCard";
import { Post } from "./PostModel";
import Pagination from "./pagination";
import defaultAxios from 'axios';
import { backendUrl, imageDeleteUrl, imageUploadUrl } from "../../../common/constantVarialble";
import Tiptap from "./tiptap";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import HashTagInput from "./hashTagInput";
import { useAuth } from "../../../AuthContext";
import '../../../styles/global.css'
import { Color } from '@tiptap/extension-color'
import ListItem from '@tiptap/extension-list-item'
import TextStyle from '@tiptap/extension-text-style'
import { EditorContent, EditorProvider, useCurrentEditor, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Heading from '@tiptap/extension-heading'
import Placeholder from "@tiptap/extension-placeholder";
import axios from "axios";
import Image from "@tiptap/extension-image";
import HashtagList from "./hashTagList";

interface PostingDetail {
    chain_code: string,
    permlink: string,
    title: string,
    content: string,
    tags: string[],
    promise_date: string,
    input_psn: string,
    create_id: string,
    input_dte: string,
    steem_sync_status: string,
    steem_author: string | null,
    steem_voter: string | null,
    steem_self_voting_status: string,
    steem_post_sync_date: string | null,
    steem_self_voting_date: string | null,
    steem_payout_estimate_date: string | null,
    steem_payout_yn: string,
    steem_payout_date: string | null,
    steem_author_payout_value: string,
    steem_author_distribution_yn: string,
    steem_author_distribution_date: string | null,
    steem_service_voting_date: string | null,
    steem_service_vote_finish_yn: string,
    hive_sync_status: string,
    hive_author: string | null,
    hive_voter: string | null,
    hive_self_voting_status: string,
    hive_post_sync_date: string | null,
    hive_self_voting_date: string | null,
    hive_payout_estimate_date: string | null,
    hive_payout_yn: string,
    hive_payout_date: string | null,
    hive_author_payout_value: string,
    hive_author_distribution_yn: string,
    hive_author_distribution_date: string | null,
    hive_service_voting_date: string | null,
    hive_service_vote_finish_y: string
}

interface DescriptionProps {
    title: string,
    description: string,
    option: string // basic, steem, hive
}

/**
 * 서비스 포스팅 상세 설명 컴포넌트
 * 
 * @param title: 상세설명 항목(제목)
 * @param description: 상세 설명
 * @param option: basic, steem, hive
 * @returns Component
 */
const Description: React.FC<DescriptionProps> = ({ title, description, option }) => {
    return (
        <div className="flex flex-row gap-4 items-center ">
            <p className={`text-sm font-thin ${option == 'steem' ? 'text-sky-500' : option == 'hive' ? 'text-orange-500' : 'text-green-400'}`}>{title} :</p>
            <p className={`italic text-xs font-thin ${option == 'steem' ? 'text-cyan-300' : option == 'hive' ? 'text-amber-300' : 'text-lime-400'}`}>{description}</p>
        </div>
    );
}

function PostingDetailScreen() {
    const [searchParams] = useSearchParams();
    const permlink = searchParams.get("permlink");
    const [posting, setPosting] = useState<PostingDetail | null>(null);
    const { user } = useAuth(); // 포스팅 작성 User ID    
    const navigate = useNavigate();

    const editorProps = {
        attributes: {
            class: 'text-xs leading-6 text-white  focus:outline-none bg-transparent min-h-96 mb-2 ',
        } //p
    };
    const extensions = [
        Color.configure({ types: [TextStyle.name, ListItem.name] }),
        //TextStyle.configure({ types: [ListItem.name] }),
        Heading.configure({
            levels: [1, 2, 3, 4, 5, 6],
        }),
        StarterKit.configure({
            bulletList: {
                keepMarks: true,
                keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
            },
            orderedList: {
                keepMarks: true,
                keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
            },
        }),
        Placeholder.configure({
            placeholder: "Please input your detail",
        }),
        Image.configure({
            inline: true,
            HTMLAttributes: {
                class: "custom-img-class",
            },
        }),
    ];

    const mainEditor = useEditor({
        editorProps,
        extensions,
        editable: false,
        // injectCSS: false,
        content: posting?.content ?? "",
    });

    const fetchPosting = async (permlink: string) => {
        try {
            if (permlink == "") return;
            const { data } = await axios({
                method: "post",
                url: backendUrl,
                data: {
                    program_id: "PERCENT-ENG-POSTING-S-LSTSERVICEPOSTINGBYPERMLINK",
                    params: {
                        p_permlink: permlink
                    },
                    initial_start_index: 0,
                    page_index: -1,
                    item_count: 0,
                },
            });
            return data;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    // 포스팅 디테일 로드
    useEffect(() => {
        const loadPosting = async () => {
            let posting = await fetchPosting(permlink ?? "");
            if (posting && posting.data.length > 0) {
                setPosting(posting.data[0]);
            }
        }

        loadPosting();
    }, [permlink])

    // 포스팅 정보 업데이트시 에디터의 본문(Content) 업데이트
    useEffect(() => {
        if (posting) {
            mainEditor?.commands.setContent(posting.content);
        }
    }, [posting, mainEditor]);

    // 게시물 수정 클릭 
    const onUpdateClick = () => {
        if (posting?.permlink) {
            navigate(`/admin/edit-post?permlink=${permlink}`);
        }
    }

    // 게시물 삭제 클릭
    const onDeleteClick = async () => {
        try {
            // 1. 게시물 삭제
            if (permlink == "") return;
            const { data } = await axios({
                method: "post",
                url: backendUrl,
                data: {
                    program_id: "PERCENT-ENG-POSTING-P-DLTSERVICEPOSTING",
                    params: {
                        p_perm_link: permlink,
                        p_user_id: user?.uid
                    },
                    initial_start_index: 0,
                    page_index: -1,
                    item_count: 0,
                },
            });

            // 2. 서비스포스팅 홈화면으로 이동            
            navigate("/admin/create-service-post-home?page=1");
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    return (<div className="flex flex-1 min-h-screen bg-gray-800 py-12 px-24 flex-col">

        <div>
            {
                user?.uid == posting?.create_id ?
                    <div className="flex flex-row gap-2 mb-4 justify-end">

                        <button className="group relative h-8 w-28 overflow-hidden rounded-2xl bg-cyan-500 text-sm font-medium text-white" onClick={onUpdateClick}>
                            Update
                            <div className="absolute inset-0 h-full w-full scale-0 rounded-2xl transition-all duration-300 group-hover:scale-100 group-hover:bg-white/30"></div>
                        </button>
                        <button className="group relative h-8 w-28 overflow-hidden rounded-2xl bg-amber-500 text-sm font-medium text-white" onClick={onDeleteClick}>
                            Delete
                            <div className="absolute inset-0 h-full w-full scale-0 rounded-2xl transition-all duration-300 group-hover:scale-100 group-hover:bg-white/30"></div>
                        </button>
                    </div> : null
            }
            <div className="flex flex-row w-full bg-gray-700 rounded-lg">
                <div className="flex flex-col w-3/5 m-2 px-6">
                    <div className="flex flex-row justify-between">
                        <p className="text-white text-xl font-bold my-2">{posting?.title}</p>

                    </div>
                    <EditorContent editor={mainEditor} />
                    <div className="mb-4">
                        <HashtagList hashState={posting?.tags ?? []} />
                    </div>
                </div>
                <div className="flex flex-col w-2/5 bg-gray-900 py-4 px-6 rounded-r-lg gap-1">
                    <p className="text-gray-300 text-xl font-bold mb-4">Service Posting Information</p>
                    <Description
                        title="Input person"
                        description={posting?.input_psn ?? ""}
                        option="basic"
                    />
                    <Description
                        title="Input date"
                        description={posting?.input_dte ?? ""}
                        option="basic"
                    />
                    <Description
                        title="Promise posting date"
                        description={posting?.promise_date ?? ""}
                        option="basic"
                    />
                    <div className="my-2" />
                    <Description
                        title="Steem sync status"
                        description={posting?.steem_sync_status ?? ""}
                        option="steem"
                    />
                    <Description
                        title="Steem sync date"
                        description={posting?.steem_post_sync_date ?? "None"}
                        option="steem"
                    />
                    <Description
                        title="Steem self-voting status"
                        description={posting?.steem_self_voting_status ?? ""}
                        option="steem"
                    />

                    <Description
                        title="Steem self-voting date"
                        description={posting?.steem_self_voting_date ?? "None"}
                        option="steem"
                    />
                    <Description
                        title="Steem service voting end date"
                        description={posting?.steem_service_voting_date ?? "None"}
                        option="steem"
                    />
                    {
                        posting?.steem_payout_date ?
                            <Description
                                title="Steem payout date"
                                description={posting?.steem_payout_date ?? "None"}
                                option="steem"
                            /> :
                            <Description
                                title="Steem payout estimate date"
                                description={posting?.steem_payout_estimate_date ?? "None"}
                                option="steem"
                            />
                    }
                    <Description
                        title="Steem author reward"
                        description={posting?.steem_author_payout_value ?? "0"}
                        option="steem"
                    />
                    <Description
                        title="Steem author reward distribution date"
                        description={posting?.steem_author_distribution_date ?? "Not yet"}
                        option="steem"
                    />
                    <div className="my-2" />
                    <Description
                        title="Hive sync status"
                        description={posting?.hive_sync_status ?? ""}
                        option="hive"
                    />
                    <Description
                        title="Hive sync date"
                        description={posting?.hive_post_sync_date ?? "None"}
                        option="hive"
                    />
                    <Description
                        title="Hive self-voting status"
                        description={posting?.hive_self_voting_status ?? ""}
                        option="hive"
                    />

                    <Description
                        title="Hive self-voting date"
                        description={posting?.hive_self_voting_date ?? "None"}
                        option="hive"
                    />
                    <Description
                        title="Hive service voting end date"
                        description={posting?.hive_service_voting_date ?? "None"}
                        option="hive"
                    />
                    {
                        posting?.hive_payout_date ?
                            <Description
                                title="Hive payout date"
                                description={posting?.hive_payout_date ?? "None"}
                                option="hive"
                            /> :
                            <Description
                                title="Hive payout estimate date"
                                description={posting?.hive_payout_estimate_date ?? "None"}
                                option="hive"
                            />
                    }
                    <Description
                        title="Hive author reward"
                        description={posting?.hive_author_payout_value ?? "0"}
                        option="hive"
                    />
                    <Description
                        title="Hive author reward distribution date"
                        description={posting?.hive_author_distribution_date ?? "Not yet"}
                        option="hive"
                    />

                </div>
            </div>



        </div>
    </div>);
}

export { PostingDetailScreen };